import { useState, useEffect, useCallback } from "react";
import { fetchMaintenancePublic } from "../services/maintenanceClient";
import { fetchFeatureFlagsPublic } from "../services/featureFlagsClient";
import { DEFAULT_FEATURE_FLAGS } from "../context/FeatureFlagsContext";

function normalizeFlagsPayload(data) {
  if (!data || typeof data !== "object") return { ...DEFAULT_FEATURE_FLAGS };
  return {
    codEnabled: data.codEnabled !== false,
    razorpayEnabled: data.razorpayEnabled !== false,
    wishlistEnabled: data.wishlistEnabled !== false,
    googleLoginUserEnabled: data.googleLoginUserEnabled !== false,
    googleLoginVendorEnabled: data.googleLoginVendorEnabled !== false,
  };
}

export function useMaintenanceGate() {
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [headline, setHeadline] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [rechecking, setRechecking] = useState(false);
  const [featureFlags, setFeatureFlags] = useState(DEFAULT_FEATURE_FLAGS);

  const refetch = useCallback(async () => {
    setRechecking(true);
    try {
      const [m, f] = await Promise.allSettled([
        fetchMaintenancePublic(),
        fetchFeatureFlagsPublic(),
      ]);
      if (m.status === "fulfilled") {
        const data = m.value;
        setEnabled(Boolean(data.enabled));
        setHeadline(String(data.headline || "").trim());
        setBodyText(String(data.bodyText || "").trim());
      } else {
        setEnabled(false);
        setHeadline("");
        setBodyText("");
      }
      if (f.status === "fulfilled") {
        setFeatureFlags(normalizeFlagsPayload(f.value));
      }
    } catch {
      setEnabled(false);
      setHeadline("");
      setBodyText("");
    } finally {
      setRechecking(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const [m, f] = await Promise.allSettled([
          fetchMaintenancePublic(),
          fetchFeatureFlagsPublic(),
        ]);
        if (cancelled) return;
        if (m.status === "fulfilled") {
          const data = m.value;
          setEnabled(Boolean(data.enabled));
          setHeadline(String(data.headline || "").trim());
          setBodyText(String(data.bodyText || "").trim());
        } else {
          setEnabled(false);
          setHeadline("");
          setBodyText("");
        }
        if (f.status === "fulfilled") {
          setFeatureFlags(normalizeFlagsPayload(f.value));
        }
      } catch {
        if (!cancelled) {
          setEnabled(false);
          setHeadline("");
          setBodyText("");
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    ready,
    enabled,
    headline,
    bodyText,
    refetch,
    rechecking,
    featureFlags,
  };
}
